import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import StatTiles from "@/components/usage/StatTiles";
import UsageCharts from "@/components/usage/UsageCharts";
import SizingCard from "@/components/sizing/SizingCard";
import PanelArray from "@/components/sizing/PanelArray";

export default function AdminDeviceDetail() {
  const { id } = useParams();
  const [device, setDevice] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.get(`/admin/devices/${id}`), api.get(`/admin/devices/${id}/sizing`)]);
      setDevice(d.data); setData(s.data);
    } catch (e) { toast.error(errMsg(e)); setErr(true); }
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (err) return <div className="h-full flex flex-col items-center justify-center gap-3"><p className="kl-label">Device not found</p><Link to="/devices" className="kl-btn-outline">Back to devices</Link></div>;
  if (!device || !data) return <div className="h-full flex items-center justify-center kl-label">Loading node…</div>;

  const { usage, sizing, explanation } = data;
  const online = device.status === "active";
  const hasData = usage.days > 0;

  return (
    <div className="h-full overflow-y-auto bg-white text-kl-black kl-scroll-light" data-testid="admin-device-detail">
      <div className="border-b border-kl-lineLight px-8 py-5 flex items-start justify-between gap-6 sticky top-0 bg-white z-10">
        <div className="flex items-start gap-5 min-w-0">
          <Link to="/devices" className="mt-1 p-2 border border-kl-lineLight hover:bg-kl-black hover:text-white transition-colors duration-150"><ArrowLeft size={16} /></Link>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${online ? "border-kl-active text-kl-greenDark" : "border-kl-inactive text-kl-inactive"}`}>
                <span className={`w-1.5 h-1.5 ${online ? "bg-kl-active" : "bg-kl-inactive"}`} />{online ? "online" : "offline"}
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-kl-lineLight text-kl-mutedLight">
                <Clock size={11} /> Last sync {fmt.ago(device.last_seen)}
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-kl-lineLight text-kl-mutedLight">
                <Building2 size={11} /> {device.tenant_name || "—"}
              </span>
              <span className="kl-label">{device.client_type}</span>
              <span className="font-mono text-xs text-kl-mutedLight">{device.serial}</span>
            </div>
            <h1 className="font-display font-light text-3xl mt-1 truncate">{device.client_name !== device.serial ? device.client_name : "Unassigned device"}</h1>
            <p className="text-sm text-kl-mutedLight mt-0.5">
              {[device.address, device.city, device.country].filter(Boolean).join(", ") || "No site assigned"} · SIM {device.sim_number || "—"} · {device.firmware}
            </p>
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-kl-mutedLight shrink-0 mt-1">read-only</span>
      </div>

      {!hasData ? (
        <div className="m-8 border border-dashed border-kl-lineLight p-16 text-center">
          <p className="kl-label">No telemetry yet</p>
          <h2 className="font-display text-2xl mt-2">Waiting for the first log</h2>
          <p className="text-sm text-kl-mutedLight mt-2 max-w-md mx-auto">This meter hasn't reported any readings.</p>
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-6">
          <div className="col-span-12"><StatTiles usage={usage} /></div>
          <div className="col-span-12 xl:col-span-8 space-y-6">
            <UsageCharts usage={usage} />
            <PanelArray sizing={sizing} />
            {explanation && (
              <div className="kl-panel-dark p-6">
                <p className="kl-label-dark">Diagnostic output</p>
                <h3 className="font-display font-light text-2xl mt-1">Engineer's analysis</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/90 whitespace-pre-wrap">{explanation}</p>
              </div>
            )}
          </div>
          <div className="col-span-12 xl:col-span-4 space-y-6">
            <SizingCard sizing={sizing} />
          </div>
        </div>
      )}
    </div>
  );
}
